import { formatValue, formatWatch, type Value } from './format.js';
import { elemOf, type Expr, type Program, type Stmt, type ValueType } from './ir.js';
import { typeOf } from './types.js';

export const DEFAULT_MAX_STEPS = 2_000_000;
/** Longest text a program may build; stops runaway `join` loops from eating memory. */
const MAX_TEXT = 100_000;
/** Output kept for display; a program printing in a tight loop still ends by the step budget. */
const MAX_CONSOLE_ENTRIES = 5_000;
/** Places a program may ask for across all its lists; a runaway size stops instead of hanging. */
const MAX_ELEMENTS = 2_000_000;

export type RunStatus =
  /** Paused before a step (the highlighted block runs on the next `step()`). */
  'paused' | 'waiting-input' | 'finished' | 'error' | 'step-limit';

export type RuntimeErrorCode =
  | 'division-by-zero'
  | 'not-a-number'
  | 'not-a-whole-number'
  | 'no-value'
  | 'too-long'
  | 'step-limit'
  | 'cannot-run'
  /** A place outside the list was read or written. */
  | 'index-out-of-range'
  /** A list was asked for with a silly size (negative, or more than we can hold). */
  | 'bad-size'
  /** A character was asked for by a code outside ASCII (0–127). */
  | 'bad-code'
  /** A piece of text was asked for with a negative length. */
  | 'bad-length';

export interface RuntimeErrorInfo {
  code: RuntimeErrorCode;
  blockId: string | null;
  params: Record<string, string>;
}

export type ConsoleEntry =
  | { kind: 'out'; text: string; open: boolean }
  | { kind: 'read'; value: string }
  | { kind: 'loop'; iteration: number; vars: string };

class RunError extends Error {
  constructor(readonly info: RuntimeErrorInfo) {
    super(info.code);
  }
}

class BreakSignal extends Error {}

/** Yielded by the executor: a step boundary, or a request for more input. */
type Pause = { k: 'step'; blockId: string } | { k: 'input' };

export interface RunOptions {
  stdin?: readonly string[];
  maxSteps?: number;
  /** Record `loop k: …` trace entries (the UI turns this on while stepping). */
  trace?: boolean;
}

/** Diagnostics that do not stop a program from running. */
const NON_BLOCKING = new Set(['extra-start']);

/**
 * Executes a program one step at a time. A step is one statement, or one check of a loop or
 * `if` condition, so `while (true) {}` still spends the budget. Runs are deterministic for a
 * given stdin, which is what makes "step back" (replay to step N−1) possible.
 */
export class Run {
  status: RunStatus = 'paused';
  /** Steps started so far; while paused, the pending step is number `steps`. */
  steps = 0;
  /** Block of the pending (paused) or failing step. */
  blockId: string | null = null;
  error: RuntimeErrorInfo | null = null;
  readonly console: ConsoleEntry[] = [];
  /** Everything printed, for checking against expected output. */
  output = '';
  /** Every input line consumed, in order (for replay). */
  readonly consumed: string[] = [];
  trace: boolean;

  private readonly vars = new Map<string, Value>();
  /** The first index of each list: the student chose 0 or 1 when they made it. */
  private readonly bases = new Map<string, number>();
  /** Places handed out so far, against MAX_ELEMENTS. */
  private elements = 0;
  private readonly queue: string[];
  private readonly maxSteps: number;
  private readonly types: Record<string, ValueType>;
  private readonly gen: Generator<Pause, void, void>;
  private readonly iterations = new Map<string, number>();

  constructor(
    readonly program: Program,
    opts: RunOptions = {},
  ) {
    this.queue = [...(opts.stdin ?? [])];
    this.maxSteps = opts.maxSteps ?? DEFAULT_MAX_STEPS;
    this.trace = opts.trace ?? false;
    this.types = program.types;
    this.gen = this.main();
    const blocking = program.diagnostics.find((d) => !NON_BLOCKING.has(d.code));
    if (blocking) {
      this.fail({ code: 'cannot-run', blockId: blocking.blockId, params: {} });
    } else {
      this.advance();
    }
  }

  get done(): boolean {
    return this.status === 'finished' || this.status === 'error' || this.status === 'step-limit';
  }

  /** Run the pending step and pause before the next one. */
  step(): RunStatus {
    if (this.status === 'paused') this.advance();
    return this.status;
  }

  /** Run until finished, stopped, waiting for input, or `limit` steps have been taken. */
  runSteps(limit: number): RunStatus {
    for (let i = 0; i < limit && this.status === 'paused'; i++) this.advance();
    return this.status;
  }

  /** Supply a line typed at the inline prompt; the run resumes on the next `step()`. */
  provideInput(line: string): void {
    this.queue.push(line);
    if (this.status === 'waiting-input') this.advance();
  }

  /** Current variables, in first-use order, formatted for the watch ("n = 123"). */
  watch(): { name: string; value: string }[] {
    const out: { name: string; value: string }[] = [];
    for (const name of this.program.variables) {
      const v = this.vars.get(name);
      if (v !== undefined) out.push({ name, value: formatWatch(v, this.types[name] ?? 'int') });
    }
    return out;
  }

  private advance(): void {
    try {
      const r = this.gen.next();
      if (r.done) {
        this.status = 'finished';
        this.blockId = null;
      } else if (r.value.k === 'input') {
        this.status = 'waiting-input';
      } else {
        this.steps++;
        this.blockId = r.value.blockId;
        if (this.steps > this.maxSteps) {
          this.steps = this.maxSteps;
          this.fail({ code: 'step-limit', blockId: this.blockId, params: {} });
          return;
        }
        this.status = 'paused';
      }
    } catch (e) {
      if (e instanceof RunError) this.fail(e.info);
      else throw e;
    }
  }

  private fail(info: RuntimeErrorInfo): void {
    this.error = info;
    this.blockId = info.blockId;
    this.status = info.code === 'step-limit' ? 'step-limit' : 'error';
  }

  // ---- output

  private write(text: string): void {
    this.output += text;
    const parts = text.split('\n');
    parts.forEach((part, i) => {
      const last = this.console.at(-1);
      const endsLine = i < parts.length - 1;
      if (i === 0 && last?.kind === 'out' && last.open) {
        last.text += part;
        last.open = !endsLine;
      } else if (part !== '' || endsLine) {
        this.pushConsole({ kind: 'out', text: part, open: !endsLine });
      }
    });
  }

  private pushConsole(entry: ConsoleEntry): void {
    if (entry.kind !== 'out') {
      const last = this.console.at(-1);
      if (last?.kind === 'out') last.open = false;
    }
    if (this.console.length < MAX_CONSOLE_ENTRIES) this.console.push(entry);
  }

  // ---- execution

  private *main(): Generator<Pause, void, void> {
    yield* this.body(this.program.body);
  }

  private *body(stmts: readonly Stmt[]): Generator<Pause, void, void> {
    for (const s of stmts) yield* this.stmt(s);
  }

  private *loopBody(loop: Stmt & { body: Stmt[] }): Generator<Pause, boolean, void> {
    try {
      yield* this.body(loop.body);
    } catch (e) {
      if (e instanceof BreakSignal) return false;
      throw e;
    }
    if (this.trace) {
      const k = (this.iterations.get(loop.id) ?? 0) + 1;
      this.iterations.set(loop.id, k);
      const vars = this.watch()
        .map((w) => `${w.name} = ${w.value}`)
        .join(', ');
      this.pushConsole({ kind: 'loop', iteration: k, vars });
    }
    return true;
  }

  private *stmt(s: Stmt): Generator<Pause, void, void> {
    yield { k: 'step', blockId: s.id };
    switch (s.k) {
      case 'set': {
        if (s.value.k === 'read') {
          const v = yield* this.read(s.value.as, s.value.id);
          this.assign(s.name, v);
        } else {
          this.assign(s.name, this.eval(s.value));
        }
        break;
      }
      case 'change': {
        const cur = this.get(s.name, s.id);
        this.assign(s.name, (cur as number) + (this.eval(s.delta) as number));
        break;
      }
      case 'print': {
        const v = this.eval(s.value);
        const end = s.end === 'newline' ? '\n' : s.end === 'space' ? ' ' : '';
        this.write(formatValue(v, typeOf(s.value, this.types)) + end);
        break;
      }
      case 'if': {
        for (const b of s.branches) {
          if (this.eval(b.cond) === true) {
            yield* this.body(b.body);
            return;
          }
        }
        if (s.orElse) yield* this.body(s.orElse);
        break;
      }
      case 'repeat': {
        this.iterations.set(s.id, 0);
        // `for (k = 1; k <= times; k++)`: the count is re-checked each round, as in the C++ view.
        for (let k = 1; ; k++) {
          if (k > 1) yield { k: 'step', blockId: s.id };
          if (!(k <= (this.eval(s.times) as number))) break;
          if (!(yield* this.loopBody(s))) break;
        }
        break;
      }
      case 'while': {
        this.iterations.set(s.id, 0);
        for (let first = true; ; first = false) {
          if (!first) yield { k: 'step', blockId: s.id };
          if (this.eval(s.cond) !== true) break;
          if (!(yield* this.loopBody(s))) break;
        }
        break;
      }
      case 'for': {
        this.iterations.set(s.id, 0);
        this.assign(s.name, this.eval(s.from));
        for (let first = true; ; first = false) {
          if (!first) {
            yield { k: 'step', blockId: s.id };
            this.assign(s.name, (this.get(s.name, s.id) as number) + (s.down ? -1 : 1));
          }
          const i = this.get(s.name, s.id) as number;
          const to = this.eval(s.to) as number;
          if (s.down ? i < to : i > to) break;
          if (!(yield* this.loopBody(s))) break;
        }
        break;
      }
      case 'newArray': {
        const sizes = s.dims.map((d) => this.sizeOf(d, s.id));
        const total = sizes.reduce((a, b) => a * b, 1);
        if (total + this.elements > MAX_ELEMENTS) {
          throw new RunError({ code: 'bad-size', blockId: s.id, params: { name: s.name } });
        }
        this.elements += total;
        const zero: Value = s.elem === 'string' ? '' : s.elem === 'bool' ? false : 0;
        const [rows, cols] = sizes;
        const list: Value[] =
          cols === undefined
            ? new Array<Value>(rows!).fill(zero)
            : Array.from({ length: rows! }, () => new Array<Value>(cols).fill(zero));
        this.vars.set(s.name, list);
        this.bases.set(s.name, s.base);
        break;
      }
      case 'setAt': {
        const place = this.place(s.name, s.index, s.index2, s.id);
        const v =
          s.value.k === 'read' ? yield* this.read(s.value.as, s.value.id) : this.eval(s.value);
        // A list of whole numbers truncates, exactly like an `int` variable does.
        place.list[place.at] =
          elemOf(this.types[s.name] ?? 'int') === 'int' && typeof v === 'number'
            ? Math.trunc(v)
            : v;
        break;
      }
      case 'break':
        throw new BreakSignal();
    }
  }

  private *read(as: 'int' | 'double' | 'text', blockId: string): Generator<Pause, Value, void> {
    for (;;) {
      // Numbers skip blank lines, like `cin >> n`; text takes the next line as it is.
      while (as !== 'text' && this.queue.length > 0 && this.queue[0]!.trim() === '') {
        this.consumed.push(this.queue.shift()!);
      }
      if (this.queue.length > 0) break;
      yield { k: 'input' };
    }
    const raw = this.queue.shift()!;
    this.consumed.push(raw);
    const line = as === 'text' ? raw : raw.trim();
    this.pushConsole({ kind: 'read', value: line });
    if (as === 'text') return line;
    if (as === 'int') {
      if (!/^[+-]?\d+$/.test(line)) {
        throw new RunError({ code: 'not-a-whole-number', blockId, params: { value: line } });
      }
      return Number(line);
    }
    if (!/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(line)) {
      throw new RunError({ code: 'not-a-number', blockId, params: { value: line } });
    }
    return Number(line);
  }

  /** The size a `make list` asked for: a whole number, and not a silly one. */
  private sizeOf(e: Expr, blockId: string): number {
    const n = this.eval(e);
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n > MAX_ELEMENTS) {
      throw new RunError({ code: 'bad-size', blockId, params: { value: String(n) } });
    }
    return n;
  }

  /**
   * Resolves `a[i]` (or `a[i][j]`) to the list and the position inside it, in the student's own
   * numbering. Asking for a place that is not there stops the run — a silent 0 would hide the
   * mistake, and this is where most array bugs live.
   */
  private place(
    name: string,
    index: Expr,
    index2: Expr | null,
    blockId: string,
  ): { list: Value[]; at: number } {
    const base = this.bases.get(name) ?? 0;
    const outer = this.get(name, blockId);
    if (!Array.isArray(outer)) throw new RunError({ code: 'cannot-run', blockId, params: {} });
    const i = this.indexOf(index, base, outer.length, name, blockId);
    if (!index2) return { list: outer, at: i };
    const row = outer[i];
    if (!Array.isArray(row)) throw new RunError({ code: 'cannot-run', blockId, params: {} });
    const j = this.indexOf(index2, base, row.length, name, blockId);
    return { list: row, at: j };
  }

  private indexOf(
    e: Expr,
    base: number,
    length: number,
    name: string,
    blockId: string,
    text = false,
  ): number {
    const raw = this.eval(e);
    const idx = typeof raw === 'number' ? Math.trunc(raw) : NaN;
    const at = idx - base;
    if (!Number.isFinite(at) || at < 0 || at >= length) {
      throw new RunError({
        code: 'index-out-of-range',
        blockId,
        params: {
          name,
          index: String(raw),
          first: String(base),
          last: String(base + length - 1),
          // The message then speaks of positions in a text rather than places in a list.
          ...(text ? { text: '1' } : {}),
        },
      });
    }
    return at;
  }

  private get(name: string, blockId: string): Value {
    const v = this.vars.get(name);
    if (v === undefined) throw new RunError({ code: 'no-value', blockId, params: { name } });
    return v;
  }

  private assign(name: string, v: Value): void {
    // An `int` variable truncates what it is given, like assignment in C++.
    this.vars.set(name, this.types[name] === 'int' && typeof v === 'number' ? Math.trunc(v) : v);
  }

  private eval(e: Expr): Value {
    switch (e.k) {
      case 'num':
      case 'text':
      case 'bool':
        return e.value;
      case 'var':
        return this.get(e.name, e.id);
      case 'read':
        // Only reachable in programs with a `read-placement` diagnostic, which never run.
        throw new RunError({ code: 'cannot-run', blockId: e.id, params: {} });
      case 'at': {
        const v = this.get(e.name, e.id);
        if (typeof v === 'string') {
          // A text is numbered from 0 in every language, whatever the lists use.
          return v[this.indexOf(e.index, 0, v.length, e.name, e.id, true)]!;
        }
        const place = this.place(e.name, e.index, e.index2, e.id);
        return place.list[place.at]!;
      }
      case 'code':
        return (this.eval(e.arg) as string).charCodeAt(0);
      case 'chr': {
        const n = this.eval(e.arg) as number;
        if (!Number.isInteger(n) || n < 0 || n > 127) {
          throw new RunError({ code: 'bad-code', blockId: e.id, params: { value: String(n) } });
        }
        return String.fromCharCode(n);
      }
      case 'case': {
        // Only the English letters change, exactly as C++ `toupper`/`tolower` do.
        const c = this.eval(e.arg) as string;
        if (e.upper) return c >= 'a' && c <= 'z' ? c.toUpperCase() : c;
        return c >= 'A' && c <= 'Z' ? c.toLowerCase() : c;
      }
      case 'find': {
        const needle = this.eval(e.needle) as string;
        return (this.eval(e.hay) as string).indexOf(needle);
      }
      case 'substr': {
        const text = this.eval(e.arg) as string;
        const name = e.arg.k === 'var' ? e.arg.name : '';
        // Starting right after the last character is allowed and gives "", as in C++.
        const from = this.indexOf(e.from, 0, text.length + 1, name, e.id, true);
        const len = this.eval(e.len) as number;
        if (len < 0) {
          throw new RunError({ code: 'bad-length', blockId: e.id, params: { value: String(len) } });
        }
        return text.slice(from, from + len);
      }
      case 'size': {
        const v = this.get(e.name, e.id);
        if (!Array.isArray(v))
          throw new RunError({ code: 'cannot-run', blockId: e.id, params: {} });
        const row = e.dim === 2 ? v[0] : v;
        return Array.isArray(row) ? row.length : v.length;
      }
      case 'arith': {
        const a = this.eval(e.left) as number;
        const b = this.eval(e.right) as number;
        switch (e.op) {
          case '+':
            return a + b;
          case '-':
            return a - b;
          case '*':
            return a * b;
          case '/':
            if (b === 0)
              throw new RunError({ code: 'division-by-zero', blockId: e.id, params: {} });
            return a / b;
          case 'idiv':
            if (b === 0)
              throw new RunError({ code: 'division-by-zero', blockId: e.id, params: {} });
            return Math.trunc(a / b);
          case 'mod':
            if (b === 0)
              throw new RunError({ code: 'division-by-zero', blockId: e.id, params: {} });
            return a % b;
        }
        break;
      }
      case 'compare': {
        const a = this.eval(e.left);
        const b = this.eval(e.right);
        switch (e.op) {
          case '==':
            return a === b;
          case '!=':
            return a !== b;
          case '<':
            return a < b;
          case '<=':
            return a <= b;
          case '>':
            return a > b;
          case '>=':
            return a >= b;
        }
        break;
      }
      case 'logic':
        return e.op === 'and'
          ? this.eval(e.left) === true && this.eval(e.right) === true
          : this.eval(e.left) === true || this.eval(e.right) === true;
      case 'not':
        return this.eval(e.arg) !== true;
      case 'join': {
        const text =
          formatValue(this.eval(e.left), typeOf(e.left, this.types)) +
          formatValue(this.eval(e.right), typeOf(e.right, this.types));
        if (text.length > MAX_TEXT) {
          throw new RunError({ code: 'too-long', blockId: e.id, params: {} });
        }
        return text;
      }
      case 'length':
        return String(this.eval(e.arg)).length;
      case 'missing':
        throw new RunError({ code: 'cannot-run', blockId: e.id, params: {} });
    }
    throw new Error('unreachable');
  }
}

export interface RunResult {
  status: RunStatus;
  output: string;
  steps: number;
  error: RuntimeErrorInfo | null;
}

/**
 * Run to completion without pausing (example checking, tests, the g++ comparison). A program
 * that needs more input than given stops as `waiting-input`.
 */
export function runProgram(
  program: Program,
  stdin: readonly string[],
  maxSteps?: number,
): RunResult {
  const run = new Run(program, { stdin, ...(maxSteps ? { maxSteps } : {}) });
  while (run.status === 'paused') run.runSteps(100_000);
  return { status: run.status, output: run.output, steps: run.steps, error: run.error };
}

/** stdin box text → input lines. */
export function stdinLines(text: string): string[] {
  if (text === '') return [];
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines.at(-1) === '') lines.pop();
  return lines;
}
